import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Vector3 } from 'three';

import { SettingModelSettingType, SolveCornerPlacementModelMode } from 'api/models';
import { useSolveCornerPlacement } from 'api/queries/forming/forming';
import {
    getAnchorPart,
    isTopBottomCameraView,
    raycastPlane
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { buildSolveCornerPlacementRequest } from 'app/modules/wallforming/components/setting-container/corner-placement/utils/buildSolveCornerPlacementRequest';
import { createPlaneFromCandidatePoints } from 'app/modules/wallforming/components/setting-container/hooks/manualSnapUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID } from 'shared/constants';
import { toastNotifications } from 'shared/utils/toastNotifications';

export interface ManualCornerInsertionParams {
    articleId: string;
    articleNumber: string | null;
    poiIndex: number;
}

export const usePrepareManualCornerInsertion = () => {
    const { t } = useTranslation();
    const solveCornerPlacementMutation = useSolveCornerPlacement();
    const requestIdRef = useRef(0);

    const [searchParams] = useSearchParams();
    const sessionguid = useMemo(() => searchParams.get('formingsession') ?? EMPTY_GUID, [searchParams]);
    const sessionGuidRef = useRef(sessionguid);
    useLayoutEffect(() => {
        sessionGuidRef.current = sessionguid;
    }, [sessionguid]);

    return useCallback(
        async ({ articleId, poiIndex }: ManualCornerInsertionParams): Promise<boolean> => {
            const state = usePlacementStore.getState();
            const requestId = ++requestIdRef.current;
            const requestedFromPoiIndex = state.poiSelection.current.index;
            const requestedSelectedArticleId = state.selectedArticleId;
            const isPoiRetarget = state.isAutoDragActiveRef.current && requestedFromPoiIndex !== undefined;
            state.setManualPlacementDataLoading(articleId, true);

            try {
                const request = buildSolveCornerPlacementRequest({
                    state,
                    sessionId: sessionguid,
                    systemId: import.meta.env.VITE_DEFAULT_SYSTEM_ID,
                    poiIndex,
                    mode: SolveCornerPlacementModelMode.Manual,
                    selectedCornerArticleGuid: articleId
                });
                const response = await solveCornerPlacementMutation.mutateAsync({
                    data: request
                });

                const currentState = usePlacementStore.getState();
                if (requestId !== requestIdRef.current) {
                    return false;
                }

                const isStale =
                    sessionGuidRef.current !== sessionguid ||
                    (currentState.selectedArticleId !== articleId &&
                        (!isPoiRetarget || currentState.selectedArticleId !== requestedSelectedArticleId)) ||
                    (currentState.poiSelection.current.index !== poiIndex &&
                        (!isPoiRetarget || currentState.poiSelection.current.index !== requestedFromPoiIndex)) ||
                    JSON.stringify(
                        buildSolveCornerPlacementRequest({
                            state: currentState,
                            sessionId: sessionGuidRef.current,
                            systemId: import.meta.env.VITE_DEFAULT_SYSTEM_ID,
                            poiIndex,
                            mode: SolveCornerPlacementModelMode.Manual,
                            selectedCornerArticleGuid: articleId
                        })
                    ) !== JSON.stringify(request);
                if (isStale) {
                    if (currentState.manualPlacementData.articleId === articleId) {
                        currentState.clearManualPlacementData();
                    }
                    return false;
                }

                if (!response.accepted || !response.candidatePoints || response.candidatePoints.length === 0) {
                    state.clearManualPlacementData();
                    requestAnimationFrame(() => {
                        toastNotifications.error({
                            title: t('error'),
                            message: `${response.errorCode} - ${response.message}`
                        });
                    });

                    return false;
                }

                const firstAlignment = response.candidatePoints[0]?.alignment;
                const planeNormal = firstAlignment
                    ? new Vector3(firstAlignment.x, firstAlignment.y, firstAlignment.z)
                    : new Vector3(1, 0, 0);

                const manualInsertionPlane = createPlaneFromCandidatePoints(response.candidatePoints, planeNormal);
                if (!manualInsertionPlane) {
                    state.clearManualPlacementData();
                    toastNotifications.error({ title: t('error'), message: t('errorManualCornerPlacement') });
                    return false;
                }

                const anchor = response.candidatePoints.reduce(
                    (acc, candidate) => acc.add(candidate.position),
                    new Vector3()
                );
                anchor.multiplyScalar(1 / response.candidatePoints.length);
                raycastPlane.setFromNormalAndCoplanarPoint(
                    isTopBottomCameraView(raycastPlane.normal) ? raycastPlane.normal : manualInsertionPlane.normal,
                    anchor
                );

                const selectedArticle = state.articles[articleId];
                const selectedInstanceId = Object.keys(selectedArticle?.articleInstances ?? {})[0];
                const selectedInstance = selectedInstanceId
                    ? selectedArticle?.articleInstances[selectedInstanceId]
                    : undefined;
                const liveAnchor = selectedInstance ? getAnchorPart(selectedInstance) : null;

                state.pointerPositionRef.current = (liveAnchor?.position ?? anchor).clone();
                state.setManualPlacementData(
                    articleId,
                    poiIndex,
                    response.candidatePoints,
                    manualInsertionPlane,
                    SettingModelSettingType.Corners
                );
                return true;
            } catch (error) {
                if (requestId === requestIdRef.current) {
                    // eslint-disable-next-line no-console
                    console.error('Manual corner placement request failed', error);
                    state.clearManualPlacementData();
                    toastNotifications.error({ title: t('error'), message: t('errorManualCornerPlacement') });
                }
                return false;
            } finally {
                if (requestId === requestIdRef.current) {
                    usePlacementStore.setState({ waitForPlacementData: false });
                }
            }
        },
        [sessionguid, solveCornerPlacementMutation, t]
    );
};
